import * as AccountApi from 'Agent/Accounts/api';
import {ReportRequestDto} from 'Agent/Accounts/api';
import {AccountFilters} from 'Agent/Accounts/ducks/account';
import {AccountDetail} from 'Agent/Accounts/models/AccountDetail';
import {ValidEnrollmentApplication} from 'Agent/Accounts/models/EnrollmentApplication';
import {AsyncOperation} from 'Core/models/Async';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {get, getAxiosConfig, handleResponse, withCancellation} from 'Shared/utilities/fetchHelpers';
import axiosInstance from 'Shared/utilities/axiosInstance';
import {toSortQuerystringParams, Sort} from 'Shared/components/PagedView/Table/Sort';
import {DiscrepancySummaryFilter} from 'Agent/Accounts/ducks/discrepancySummary';

const accountsApi = new AccountApi.AccountsApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const reportsApi = new AccountApi.ReportsApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const enrollmentApplicationApi = new AccountApi.EnrollmentApplicationApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);
const discrepancySummariesApi = new AccountApi.DiscrepancySummariesApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);
const imagesApi = new AccountApi.ImagesApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);

export const accountsRootUri = '/accounts';

export default {
  uploadEnrollmentApplication: (
    accountId: string,
    accountNumber: string,
    agentNumber: string,
    application: ValidEnrollmentApplication,
    encryptedIdentifier: string
  ) =>
    enrollmentApplicationApi
      .enrollmentApplicationUploadDocument(
        accountId,
        '1',
        accountNumber,
        agentNumber,
        application.enrollmentType,
        application.file,
        {
          params: {encryptedIdentifier},
          ...getAxiosConfig(undefined),
        }
      )
      .then(handleResponse),
  requestReport: (
    accountNumber: string,
    portalUserId: string,
    email: string,
    sourceId: string,
    encryptedIdentifier: string
  ) =>
    withCancellation(cancelToken =>
      reportsApi
        .reportsRequestReport(
          '1',
          {accountNumber, portalUserId, email, sourceId},
          {params: {encryptedIdentifier}, ...getAxiosConfig(cancelToken)}
        )
        .then(handleResponse)
    ),
  getAccountDetails: (encryptedIdentifier: string, accountId: string, accountNumber: string) =>
    get<AccountDetail>(
      `${accountsRootUri}/${accountId}/accountNumber/${accountNumber}/details${addFilterParameters({
        encryptedIdentifier,
      })}`
    ),
  getAccounts: (
    agentNumbers: string[],
    isNewYork: boolean,
    encryptedIdentifier: string,
    filter?: AccountFilters,
    sort?: Sort
  ) =>
    withCancellation(cancelToken =>
      accountsApi
        .accountsGet('1', {
          params: {agentNumbers, isNewYork, encryptedIdentifier, ...filter, ...(sort && toSortQuerystringParams(sort))},
          ...getAxiosConfig(cancelToken),
        })
        .then(handleResponse)
    ),
  getPolicyInformationReports: (encryptedIdentifier: string, userId: string) =>
    withCancellation(cancelToken =>
      reportsApi.reportsGet(userId, '1', false, {params: {encryptedIdentifier}, ...getAxiosConfig(cancelToken)})
    ).then(handleResponse) as AsyncOperation<ReportRequestDto[]>,
  deletePolicyInformationReport: (encryptedIdentifier: string, userId: string, reportId: number) =>
    withCancellation(cancelToken =>
      reportsApi.reportsRemoveReport(
        '1',
        {portalUserId: userId, reportId},
        {params: {encryptedIdentifier}, ...getAxiosConfig(cancelToken)}
      )
    ).then(handleResponse),
  getDiscrepancySummaries: (
    encryptedIdentifier: string,
    accountNumber: string,
    sourceId: number,
    paymentDate: Date,
    discrepancySummaryFilter: DiscrepancySummaryFilter
  ) =>
    withCancellation(cancelToken =>
      discrepancySummariesApi
        .discrepancySummariesGet('1', {
          params: {
            encryptedIdentifier,
            accountNumber,
            sourceId,
            paymentDate: paymentDate.toISOString(),
            ...discrepancySummaryFilter,
          },
          ...getAxiosConfig(cancelToken),
        })
        .then(handleResponse)
    ),
  getImages: (encryptedIdentifier: string, accountId: string) =>
    imagesApi
      .getAccountImagesById(accountId, '1', {params: {encryptedIdentifier}, ...getAxiosConfig()})
      .then(handleResponse),
};
